import * as React from 'react'
import { Progress } from 'reactstrap'
import {
  CreateInvoice,
  API,
  CreateInvoiceLine,
  PurchaseOrderReceipt,
  IProductFoundInProductListing,
  Province,
  InventorySearchFilter,
  IMultiLocationInventory,
} from '@getgreenline/homi-shared'
import { CurrentCompanyStore } from '../../../../../../stores/CurrentCompanyStore'
import { parseErrorMsg, uploadFileToS3Bucket, sleep } from '../../../../../../utilities/helpers'
import { inject, observer } from 'mobx-react'
import { ReceiptProducts } from './ReceiptProducts'
import { ReceiptDropzone } from './ReceiptDropzone'
import { Modal, Tabs, Select, message, Descriptions, Button, Icon, Switch } from 'antd'
import './importPOModal.scss'
import { LowStockTable } from './LowStockTable'
import { SelectValue } from 'antd/lib/select'
import { Routes } from '../../../../../../constants/Routes'
import { BlazeButton } from '../../../../../../components/CustomButtons/BlazeButton'
import { CreatePOReceipt } from './CreatePOReceipt'
import { UploadFile } from 'antd/lib/upload/interface'
import { Track } from '../../../../../../Track'
import { CurrencyInput } from '../../../../../../components/CurrencyInput'
import { CurrentUserStore } from '../../../../../../stores/CurrentUserStore'
import { FeatureToggle } from '../../../../../../constants/FeatureToggles'
import { OrderList } from './ImportSteps/OrderList'
import { CreateInvoiceDetail } from '../../InvoiceDetailStore'
import { BLAZE_COMPANY } from '../../../../../../constants/general'
import _ from 'lodash'

const { TabPane } = Tabs
const { Option } = Select

export type POUpdate = typeof BLAZE_COMPANY | Province | null

export enum POReceiptExtension {
  ABxls = 'xls',
  ABxlsx = 'xlsx',
  BC = 'xls',
  BCxlsx = 'xlsx',
  BCxml = 'xml',
  ONxls = 'xls',
  ONxlsx = 'xlsx',
  BLAZE_COMPANY = 'csv',
}

interface IFileObject {
  originFileObj: File | Blob
  fullFileName: string
  fileExtension: string
  contentType: string
}

enum ImportStep {
  SHOW_INTRO,
  DROP_FILE,
  SHOW_ORDER_LIST,
  SHOW_PROGRESS_BAR,
  SHOW_RESULT,
}

interface State {
  isRefreshing: boolean
  receiptResponse?: PurchaseOrderReceipt
  uploadStepsTaken: number
  progressMessage: string
  receiptFileName?: string
  uploadType: POUpdate
  isABxlsxImported: boolean
  shippingFee: number
  categoryWarningCount: number
  canCreateProductsInPO: boolean
  importStep: ImportStep
  fileObject?: IFileObject
}

interface Props {
  createInvoice: CreateInvoiceDetail
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
  onHide: () => void
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class ImportPOModal extends React.Component<Props, State> {
  readonly company = this.props.currentCompanyStore!.company!
  user = this.props.currentUserStore!.currentUser!

  searchQueryFilter = new InventorySearchFilter({
    sku: undefined,
    searchQuery: '',
    limit: 100,
    offset: 0,
    hideEmptyQuantities: false,
    lowStock: true,
    selectedCategoryId: undefined,
    selectedSupplierId: undefined,
    locationIds: [this.props.createInvoice.locationId],
  })

  createPOReceipt = new CreatePOReceipt()

  private uploadTotalSteps = 4

  constructor(props: Props) {
    super(props)

    this.state = {
      importStep: ImportStep.SHOW_INTRO,
      isRefreshing: false,
      receiptResponse: undefined,
      uploadStepsTaken: 0,
      progressMessage: '',
      receiptFileName: undefined,
      uploadType: null,
      isABxlsxImported: false,
      shippingFee: 0,
      categoryWarningCount: 0,
      canCreateProductsInPO: false,
    }
  }

  componentDidMount = async () => {
    try {
      await this.props.currentCompanyStore!.productStore!.getCategories()
      await this.props.currentCompanyStore!.supplierStore!.getSuppliers()

      const canCreateProductsInPO = await this.props.currentCompanyStore!.isFeatureEnabled(
        FeatureToggle.CREATE_PO_PRODUCTS,
      )

      this.setState({ canCreateProductsInPO })
    } catch (err) {
      message.error(parseErrorMsg(err) || 'There was an error loading. Please try again.')
    }

    if (!this.props.createInvoice.locationId) {
      message.error('Please select a location first')
    }
  }

  closeModal = () => {
    if (window.confirm('Are you sure? Any unsaved results will be deleted')) {
      this.props.onHide()
    }
  }

  setInvoiceLineValues = (
    line: CreateInvoiceLine,
    productToAdd: IProductFoundInProductListing,
    isDuplicate: boolean,
  ) => {
    const { total, caseCount, quantityPerCase, batchNumber } = productToAdd

    const individualQuantity = quantityPerCase && caseCount * quantityPerCase
    const newLineQuantity =
      isDuplicate && line.quantity && individualQuantity // If there is a duplicate, add on top of the existing quantity
        ? line.quantity + individualQuantity
        : individualQuantity

    const newTotal = isDuplicate ? line.total + total : total // If there is a duplicate, add on top of the existing total

    if (line.isBatchTracked) {
      if (_.isNil(batchNumber)) {
        line.setQuantity(newLineQuantity)
      } else {
        if (individualQuantity) {
          this.setBatchTrackedLineValue(line, individualQuantity, batchNumber)
        }
      }
    } else {
      line.setQuantity(newLineQuantity)
    }

    line.setTotal(newTotal)
  }

  setBatchTrackedLineValue = (line: CreateInvoiceLine, quantity: number, batchNumber: string) => {
    const targetBatchIndex = line.batches.findIndex((batch) => batch.batchNumber === batchNumber)

    if (targetBatchIndex === -1) {
      // batchNumber could be number or string
      line.addBatch({ batchNumber: _.toString(batchNumber), quantity })
    } else {
      line.batches[targetBatchIndex].quantity = quantity
    }

    line.setQuantity(line.batchQuantitySum)
  }

  addInvoiceFees = (costs: Array<{ costType: string; costInCent: number }>) => {
    const shippingFeeRegex = new RegExp('Shipping', 'i')

    // Check for duplicate fee in invoice
    const existingShippingFee = this.props.createInvoice.fees.find((fee) =>
      fee.name.match(shippingFeeRegex),
    )
    if (existingShippingFee) {
      return
    }

    const shippingFeeLine = costs.find((cost) => cost.costType.match(shippingFeeRegex))
    if (!shippingFeeLine) {
      return
    }

    const { costType, costInCent } = shippingFeeLine
    const invoiceFee = this.props.createInvoice.addFee()

    invoiceFee.setName(costType)
    invoiceFee.setTotal(costInCent)
  }

  setExistingLineProducts = (): Map<string, CreateInvoiceLine> => {
    const existingLineProducts = new Map<string, CreateInvoiceLine>()

    this.props.createInvoice.lines.forEach((lineProduct) => {
      if (lineProduct.productId !== null) {
        existingLineProducts.set(lineProduct.productId, lineProduct)
      }
    })

    return existingLineProducts
  }

  onReceiptImport = (receiptResponse: PurchaseOrderReceipt) => {
    this.props.createInvoice.setIsEditMode(true)
    this.addReceiptProductsToInvoice(receiptResponse)
    this.props.onHide()
  }

  addReceiptProductsToInvoice = (receiptResponse: PurchaseOrderReceipt) => {
    const { createInvoice } = this.props

    const existingLineProducts = this.setExistingLineProducts()
    const duplicateProductsInReceipt: { [key: string]: boolean } = {}

    for (const foundProduct of receiptResponse.foundInProductListing) {
      if (foundProduct.caseCount === 0) {
        continue
      }

      const { productDetail } = foundProduct

      // Check if line already exists
      const existingProduct = existingLineProducts.get(productDetail.id)

      const existingLine = existingProduct || createInvoice.addProduct(productDetail)

      if (existingLine.productId !== null && !existingLineProducts.get(existingLine.productId)) {
        /**
         * There may be invoice lines with the same product id BUT with different lot numbers. In that case, add the product id to `existingLineProducts`
         * so that the lot numbers can be added to a single invoice line
         */
        existingLineProducts.set(existingLine.productId, existingLine)
      }

      // Keep track of whether there are duplicate products in the receipt
      duplicateProductsInReceipt[productDetail.id] = duplicateProductsInReceipt.hasOwnProperty(
        productDetail.id,
      )

      const isDuplicate = duplicateProductsInReceipt[productDetail.id]

      this.setInvoiceLineValues(existingLine, foundProduct, isDuplicate)
    }

    const inputShippingFee = { costType: 'Shipping & Handling', costInCent: this.state.shippingFee }
    this.addInvoiceFees([...receiptResponse.costs, inputShippingFee])

    message.success('Ordered products successfully added to the invoice')
  }

  xhrOnload = async (fileName: string) => {
    try {
      const receiptResponse = await API.processReceipt(this.company.id, {
        fileName,
        orderNumber: this.props.createInvoice.orderNumber,
      })
      Track.track('dashboard:purchaseOrder_import', {
        type: this.state.uploadType,
        status: 'success',
      })

      let categoryWarningCount = 0
      if (receiptResponse) {
        categoryWarningCount = receiptResponse.foundInProductListing.filter(
          (product) => !product.productDetail.categoryId,
        ).length
      }

      await this.setState({
        receiptResponse,
        progressMessage: 'Receipt analysis complete',
        uploadStepsTaken: this.state.uploadStepsTaken + 1,
        categoryWarningCount,
      })

      // Delay for 1 second so that there is enough time to show the progress message at 100%
      setTimeout(() => this.setState({ importStep: ImportStep.SHOW_RESULT }), 1000)

      message.success(`${this.state.uploadType} receipt successfully analyzed`)
    } catch (error) {
      const errorMessage = parseErrorMsg(error) || 'An error occurred while processing your file'

      Track.track('dashboard:purchaseOrder_import', {
        type: this.state.uploadType,
        status: 'error',
      })

      message.error(errorMessage, 10)

      this.setState({ importStep: ImportStep.DROP_FILE })
    }
  }

  uploadFile = (
    file: File | Blob,
    fileName: string,
    signedUrl: string,
    contentType: string,
  ): void => {
    const xhr = uploadFileToS3Bucket(file, signedUrl, contentType)

    xhr.onload = async () => {
      if (xhr.status !== 200) {
        return message.error('An error occurred while uploading your file')
      }

      await this.setState({
        progressMessage: 'Analyzing the receipt...',
        uploadStepsTaken: this.state.uploadStepsTaken + 1,
      })

      await this.xhrOnload(fileName)
    }
  }

  canUploadPODocument = (province: string | null) => {
    if (province === null) {
      return false
    }

    return ['AB', 'BC', 'ON'].includes(province)
  }

  getErrorMessage(fileExtensions: POReceiptExtension[]) {
    message.error(
      <>
        <span>Please upload a file with one of the following extensions:</span>
        <ul className='no-margin-list' style={{ width: 'fit-content' }}>
          {fileExtensions.map((ext, index) => (
            <li key={index}>{ext}</li>
          ))}
        </ul>
      </>,
      10,
    )

    this.setState({ importStep: ImportStep.DROP_FILE })
  }

  checkAndParseDroppedFile = async (file: UploadFile<any>) => {
    const { name, type, originFileObj } = file
    const fileExtension = name.split('.').pop()

    if (!fileExtension || this.incorrectFileExtension(fileExtension)) {
      if (this.state.uploadType === 'AB') {
        this.getErrorMessage([POReceiptExtension.ABxls, POReceiptExtension.ABxlsx])
      } else if (this.state.uploadType === 'BC') {
        this.getErrorMessage([POReceiptExtension.BC, POReceiptExtension.BCxlsx])
      } else if (this.state.uploadType === 'ON') {
        this.getErrorMessage([POReceiptExtension.ONxls, POReceiptExtension.ONxlsx])
      } else if (this.state.uploadType === BLAZE_COMPANY) {
        this.getErrorMessage([POReceiptExtension.BLAZE_COMPANY])
      }

      return
    }

    if (!originFileObj) {
      this.setState({ importStep: ImportStep.DROP_FILE })
      throw new Error(
        `The originFileObj of ${name} is unavailable. Please contact ${BLAZE_COMPANY}.`,
      )
    }

    await this.setState({
      fileObject: {
        originFileObj,
        fullFileName: name,
        fileExtension,
        contentType: type,
      },
    })
  }

  incorrectFileExtension = (fileExtension: string | undefined) => {
    if (this.state.uploadType === 'AB') {
      return (
        fileExtension !== POReceiptExtension.ABxls && fileExtension !== POReceiptExtension.ABxlsx
      )
    }

    if (this.state.uploadType === 'BC') {
      return (
        fileExtension !== POReceiptExtension.BCxml &&
        fileExtension !== POReceiptExtension.BC &&
        fileExtension !== POReceiptExtension.BCxlsx
      )
    }

    if (this.state.uploadType === 'ON') {
      return (
        fileExtension !== POReceiptExtension.ONxls && fileExtension !== POReceiptExtension.ONxlsx
      )
    }

    if (this.state.uploadType === BLAZE_COMPANY) {
      return fileExtension !== POReceiptExtension.BLAZE_COMPANY
    }

    return false
  }

  onDrop = async (file: UploadFile<any>) => {
    try {
      await this.setState({ uploadStepsTaken: 0 })

      await this.setState({
        importStep: ImportStep.SHOW_PROGRESS_BAR,
        progressMessage: 'Checking the file...',
        uploadStepsTaken: this.state.uploadStepsTaken + 1,
      })

      await this.checkAndParseDroppedFile(file)

      if (this.requireOrderNumberStep) {
        this.setState({
          importStep: ImportStep.SHOW_ORDER_LIST,
        })
      } else {
        await this.parseReceipt()
      }
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while checking the file', 10)

      this.setState({
        importStep: ImportStep.DROP_FILE,
      })
    }
  }

  parseReceipt = async () => {
    if (!this.state.fileObject) {
      return
    }

    const { originFileObj, fileExtension, contentType } = this.state.fileObject

    try {
      await this.setState({
        importStep: ImportStep.SHOW_PROGRESS_BAR,
        progressMessage: 'Analyzing the receipt...',
        uploadStepsTaken: this.state.uploadStepsTaken + 1,
      })

      await sleep(500) // 500ms delay to show the first progress message

      const { fileName, signedUrl } =
        await this.props.currentCompanyStore!.getSignedTempFileUploadUrl(fileExtension, contentType)

      this.setState({ receiptFileName: fileName })

      this.uploadFile(originFileObj, fileName, signedUrl, contentType)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while uploading the file', 10)
      this.setState({ importStep: ImportStep.DROP_FILE })
    }
  }

  refreshReceiptProducts = async () => {
    const { receiptFileName } = this.state
    if (!receiptFileName) {
      return
    }

    await this.setState({ isRefreshing: true })
    await this.xhrOnload(receiptFileName)
    await this.setState({ isRefreshing: false })
  }

  get requireOrderNumberStep() {
    return (
      this.state.uploadType === 'BC' &&
      this.state.fileObject?.fileExtension === POReceiptExtension.BCxml
    )
  }

  get progressRate() {
    return Math.floor((this.state.uploadStepsTaken / this.uploadTotalSteps) * 100)
  }

  get footerButton() {
    const {
      importStep,
      receiptResponse,
      isRefreshing,
      categoryWarningCount,
      canCreateProductsInPO,
    } = this.state
    const { createInvoice } = this.props
    const { lowStockProducts } = this.createPOReceipt

    if (importStep === ImportStep.SHOW_ORDER_LIST) {
      return [
        <BlazeButton
          key='back'
          buttonProps={{
            onClick: this.closeModal,
          }}
          titleProps={{
            title: 'Cancel',
          }}
        />,
        <BlazeButton
          key='continue'
          buttonProps={{
            type: 'primary',
            onClick: this.parseReceipt,
            disabled: createInvoice.orderNumber === null,
          }}
          titleProps={{
            title: 'Continue',
          }}
        />,
      ]
    }

    if (receiptResponse && importStep === ImportStep.SHOW_RESULT) {
      const footer = []
      if (this.user.isAdmin) {
        if (canCreateProductsInPO && categoryWarningCount > 0) {
          footer.push(
            <div className='mb-2'>
              <Icon
                className='mr-2'
                type='exclamation-circle'
                style={{ color: 'red', fontSize: '0.9rem' }}
              />
              <span style={{ opacity: 0.7 }}>
                {categoryWarningCount} product{categoryWarningCount === 1 ? '' : 's'} need
                {categoryWarningCount === 1 ? 's' : ''} a Category
              </span>
            </div>,
          )
        }
      }
      return [
        ...footer,
        <BlazeButton
          key='back'
          buttonProps={{
            onClick: this.closeModal,
          }}
          titleProps={{
            title: 'Cancel',
          }}
        />,
        <Button
          key='sync'
          icon='sync'
          loading={isRefreshing}
          onClick={this.refreshReceiptProducts}
        />,
        <BlazeButton
          key='import'
          buttonProps={{
            onClick: () => {
              if (this.requireOrderNumberStep && createInvoice.lines.length > 0) {
                if (
                  window.confirm(
                    'Are you sure? All current products will be removed from the invoice',
                  )
                ) {
                  this.props.createInvoice.resetLines()
                }
              }

              this.onReceiptImport(receiptResponse)
            },
            disabled: canCreateProductsInPO && (categoryWarningCount > 0 || this.isButtonDisabled),
          }}
          titleProps={{
            title: 'Import',
          }}
        />,
      ]
    }

    if (lowStockProducts.length > 0) {
      return [
        <BlazeButton
          key='import'
          buttonProps={{
            onClick: () => {
              this.addLowStockProductsToInvoice()
              this.props.onHide()
            },
          }}
          titleProps={{
            title: 'Import',
          }}
        />,
      ]
    }

    return null
  }

  get categoryOptions() {
    const dropdownOptions = (this.props.currentCompanyStore!.productStore!.categories || []).map(
      (category) => {
        return (
          <Option key={category.id} value={category.id}>
            {category.name}
          </Option>
        )
      },
    )

    return dropdownOptions
  }

  get vendorOptions() {
    const dropdownOptions = (
      this.props.currentCompanyStore!.supplierStore!.suppliersWithName || []
    ).map((category) => {
      return (
        <Option key={category.id} value={category.id}>
          {category.name}
        </Option>
      )
    })

    return dropdownOptions
  }

  get isButtonDisabled() {
    const province = this.company.province
    const { isABxlsxImported } = this.state

    return province === 'AB' && !isABxlsxImported
  }

  loadLowStockProducts = async () => {
    if (
      !this.searchQueryFilter.selectedCategoryIds &&
      !this.searchQueryFilter.selectedSupplierIds
    ) {
      return message.error('Please select at least one filter')
    }

    try {
      this.createPOReceipt.toggleLoadingLowStockProducts()

      const { multiLocationInventory } = await API.getInventoryByMultipleLocations(
        this.company.id,
        this.searchQueryFilter,
      )
      const filteredMultiLocationInventory = multiLocationInventory.filter(
        (inventoryProduct: IMultiLocationInventory) => {
          return inventoryProduct.inventories[0].restockQuantity !== null
        },
      )

      const productIds = filteredMultiLocationInventory.map(
        (inventoryProduct) => inventoryProduct.product.id,
      )
      if (multiLocationInventory.length > 0) {
        this.createPOReceipt.setLowStockProducts(filteredMultiLocationInventory)
        this.createPOReceipt.setSelectedLowStockProductIds(productIds)
      } else {
        this.createPOReceipt.setLowStockProducts([])
        this.createPOReceipt.setSelectedLowStockProductIds([])
      }
    } catch (err) {
      message.error(parseErrorMsg(err) || 'Unable to load low stock products')
    } finally {
      this.createPOReceipt.toggleLoadingLowStockProducts()
    }
  }

  onCategoriesChange = (categoryIds: SelectValue) => {
    const castedCategoryIds = categoryIds as string[]
    this.searchQueryFilter.selectedCategoryIds =
      castedCategoryIds.length > 0 ? castedCategoryIds : undefined
  }

  onSuppliersChange = (supplierIds: SelectValue) => {
    const castedSupplierIds = supplierIds as number[]
    this.searchQueryFilter.selectedSupplierIds =
      castedSupplierIds.length > 0 ? castedSupplierIds : undefined
  }

  addLowStockProductsToInvoice = () => {
    const { selectedLowStockProductIds, lowStockProducts } = this.createPOReceipt
    if (lowStockProducts.length > 0) {
      const productsToAdd = lowStockProducts.filter((inventory) =>
        selectedLowStockProductIds.includes(inventory.product.id),
      )
      productsToAdd.forEach((inventory) => {
        const invoiceLine = this.props.createInvoice.addProduct(inventory.product)
        const quantity =
          inventory.inventories[0].restockQuantity !== null
            ? inventory.inventories[0].restockQuantity - inventory.inventories[0].quantity
            : 0
        invoiceLine.setQuantity(quantity)
        invoiceLine.costPerUnit = inventory.product.purchasePrice || 0
        invoiceLine.setTotal(quantity * invoiceLine.costPerUnit)
        invoiceLine.sku = inventory.product.sku
      })
      message.success('Successfully imported')
    } else {
      message.error('Could not find any products to import')
    }
  }

  get defaultTab() {
    const province = this.company.province
    if (province === null) {
      return
    }

    if (['AB', 'BC', 'ON'].includes(province)) {
      return 'province'
    }

    return 'template'
  }

  render() {
    const {
      isRefreshing,
      receiptResponse,
      progressMessage,
      uploadType,
      categoryWarningCount,
      canCreateProductsInPO,
      importStep,
      fileObject,
    } = this.state

    return (
      <Modal
        title='Import'
        visible={true}
        onCancel={receiptResponse ? this.closeModal : this.props.onHide}
        footer={this.footerButton}
        maskClosable={false}
        width={650}
      >
        {importStep === ImportStep.SHOW_INTRO && (
          <Tabs defaultActiveKey={this.defaultTab}>
            {this.company.province !== null && ['AB', 'BC', 'ON'].includes(this.company.province) && (
              <TabPane tab='Province' key='province'>
                {this.canUploadPODocument(this.company.province) && uploadType === null && (
                  <div className='mb-3'>
                    <h5>Import from your provincial wholesaler</h5>
                    <p className='text-muted'>
                      Select this option to upload your provincial order confirmation.{' '}
                      {BLAZE_COMPANY} will find all matching products by SKU, then add them to your
                      purchase order.
                    </p>
                    {this.company.province === 'BC' && (
                      <p>
                        <b>Acceptable files</b>: ASN (.xml), order confirmation (.xls, .xlsx)
                      </p>
                    )}

                    {this.company.province === 'AB' && (
                      <p>
                        <b>Acceptable file</b>: Order confirmation ([Order number]_PackList.xlsx)
                      </p>
                    )}

                    {this.company.province === 'ON' && (
                      <p>
                        <b>Acceptable file</b>: ASN (.xls, .xlsx)
                      </p>
                    )}
                    <BlazeButton
                      buttonProps={{
                        onClick: () =>
                          this.setState({
                            uploadType: this.company.province,
                            importStep: ImportStep.DROP_FILE,
                          }),
                      }}
                      iconProps={{
                        icon: 'upload',
                      }}
                      titleProps={{
                        title: 'Upload file',
                      }}
                    />
                  </div>
                )}
              </TabPane>
            )}
            <TabPane tab='Template' key='template'>
              {uploadType === null && (
                <div>
                  <h5>Import from template</h5>
                  <p className='text-muted'>
                    Select this option to upload a csv matching the {BLAZE_COMPANY} template.
                    {BLAZE_COMPANY} will find all matching products by SKU, then add them to your
                    purchase order.
                  </p>
                  <p className='text-muted font-italic'>
                    Note: The price column should reflect the total paid for all units (or per pack)
                    on the invoice, not the per-unit cost.
                  </p>
                  <BlazeButton
                    buttonProps={{
                      onClick: () =>
                        (window.location.href =
                          'https://myhomi-images.s3-us-west-2.amazonaws.com/Greenline%2Bpurchase%2Border%2Bimport%2Btemplate.csv'),
                    }}
                    iconProps={{
                      icon: 'download',
                    }}
                    titleProps={{
                      title: 'Download template',
                    }}
                  />
                  <BlazeButton
                    buttonProps={{
                      className: 'ml-2',
                      onClick: () =>
                        this.setState({
                          uploadType: BLAZE_COMPANY,
                          importStep: ImportStep.DROP_FILE,
                        }),
                    }}
                    iconProps={{
                      icon: 'upload',
                    }}
                    titleProps={{
                      title: 'Upload csv',
                    }}
                  />
                </div>
              )}
            </TabPane>
            <TabPane tab='Low stock' key='lowstock'>
              <div className='mb-3'>
                <h5>Import from your low stock report</h5>
                <p className='text-muted'>
                  Set your low stock and restock levels on the inventory overview page. These levels
                  can be different per-location.
                </p>
                <p className='text-muted'>
                  Low stock products with recommended order quantities will be imported into this
                  PO.
                </p>
                <p className='text-muted'>
                  You can view your restock report{' '}
                  <a
                    href={Routes.DASHBOARD_RESTOCK_REPORTS(
                      this.props.currentCompanyStore!.company!.id,
                    )}
                    target='_blank'
                    rel='noreferrer'
                  >
                    here
                  </a>
                </p>
                <Select
                  mode='multiple'
                  style={{ width: '100%' }}
                  placeholder='Select categories'
                  onChange={this.onCategoriesChange}
                  className='mb-2'
                >
                  {this.categoryOptions}
                </Select>

                <Select
                  mode='multiple'
                  style={{ width: '100%' }}
                  placeholder='Select vendors'
                  onChange={this.onSuppliersChange}
                  className='mb-2'
                >
                  {this.vendorOptions}
                </Select>
                <BlazeButton
                  buttonProps={{
                    disabled: !this.props.createInvoice.locationId,
                    onClick: () => this.loadLowStockProducts(),
                  }}
                  titleProps={{
                    title: 'Generate low stock report',
                  }}
                />
              </div>
              <LowStockTable createPOReceipt={this.createPOReceipt} />
            </TabPane>
          </Tabs>
        )}

        {importStep === ImportStep.DROP_FILE && (
          <ReceiptDropzone uploadType={uploadType} onDrop={this.onDrop} />
        )}

        {importStep === ImportStep.SHOW_ORDER_LIST && fileObject?.originFileObj && (
          <OrderList
            createInvoice={this.props.createInvoice}
            originFileObj={fileObject.originFileObj}
          />
        )}

        {importStep === ImportStep.SHOW_PROGRESS_BAR && (
          <Progress animated value={this.progressRate}>
            {progressMessage}
          </Progress>
        )}

        {importStep === ImportStep.SHOW_RESULT && receiptResponse && (
          <>
            <ReceiptProducts
              receiptResponse={receiptResponse}
              company={this.company}
              refreshReceiptProducts={this.refreshReceiptProducts}
              isRefreshing={isRefreshing}
              currentCompanyStore={this.props.currentCompanyStore!}
              categoryWarningCount={categoryWarningCount}
              canCreateProductsInPO={canCreateProductsInPO}
            />

            {this.company.province === 'AB' && (
              <>
                <hr />
                <section className='mt-2'>
                  <h6 className='pt-2 pb-2'>
                    Enter <strong>Shipping & Handling</strong> fee
                    <sup className='text-danger'>* required</sup>
                  </h6>

                  <Descriptions bordered>
                    <Descriptions.Item label='Shipping & Handling'>
                      <CurrencyInput
                        className='w-100 mw-100 gr-currency-input'
                        centValue={this.state.shippingFee}
                        onChange={(value) =>
                          this.setState({ isABxlsxImported: true, shippingFee: value })
                        }
                        onlyPositiveNumber
                      />
                    </Descriptions.Item>
                  </Descriptions>
                </section>
              </>
            )}
          </>
        )}
      </Modal>
    )
  }
}
